import classNames from 'classnames';
import isNil from 'lodash/isNil';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import type {WithRouterProps} from 'react-router';

import Assignment from './Assignment';
import FilterControl, {FILTER_DESCRIPTIONS} from './FilterControl';
import {LoadingSpinner, SearchInput, Text} from '@albert-io/atomic';
import ReviewGradeBanner from '../ReviewGradeBanner/ReviewGradeBanner';
import TabEmptyState from '../TabEmptyState';
import ToDoBanner from './ToDoBanner';
import {canReadTeacherFeedback} from './teacherFeedback';
import useAssignments from './hooks/useAssignments';
import type {AssignmentFilter} from './StudentAssignment.types';
import {useStudentHomepageStore} from '../useStudentHomepageStore';

const SEARCH_DEBOUNCE_MS = 500;

interface AssignmentSearchProps {
  disabled: boolean;
  onChange: (value: string) => void;
  onReset: () => void;
}

const AssignmentSearch = ({disabled, onChange, onReset}: AssignmentSearchProps) => {
  'use memo';

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isTypingRef = useRef(false);

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    const leftSearch = !event.currentTarget.contains(event.relatedTarget);
    // Losing focus to the disabled state while results load is not the student leaving.
    if (leftSearch && !inputRef.current?.disabled) {
      isTypingRef.current = false;
    }
  };

  const handleReset = () => {
    onReset();
    // The clear button disables itself once the box is empty, which drops focus.
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  useEffect(() => {
    if (disabled || !isTypingRef.current) {
      return;
    }
    const active = document.activeElement;
    // Loading drops the box's focus to the page; focus anywhere else means the student moved on.
    if (!active || active === document.body) {
      inputRef.current?.focus();
    } else if (!wrapperRef.current?.contains(active)) {
      isTypingRef.current = false;
    }
  }, [disabled]);

  return (
    <div
      ref={wrapperRef}
      onBlur={handleBlur}
      onFocus={() => {
        isTypingRef.current = true;
      }}
    >
      <SearchInput
        ref={inputRef}
        aria-label='Search assignments'
        disabled={disabled}
        onChange={(_e, value) => onChange(value)}
        onReset={handleReset}
        placeholder='Search assignments'
        rectangle
        searchIconClassName='content-secondary!'
        variant
      />
    </div>
  );
};
function getEmptyStateCopy(filter: AssignmentFilter, hasOtherAssignments: boolean): string {
  const defaultCopy = 'Your upcoming assignments will appear here.';
  if (!hasOtherAssignments) {
    return defaultCopy;
  }

  switch (filter) {
    case 'toDo':
      return 'No upcoming assignments.';
    case 'open':
      return 'No open assignments.';
    case 'closed':
      return 'No closed assignments.';
    default:
      return defaultCopy;
  }
}

export default function Assignments({params}: WithRouterProps) {
  'use memo';

  const areClassroomsLoading = useStudentHomepageStore((state) => state.areClassroomsLoading);

  const classroomId: string | undefined = params.classroomId;

  const paginationTrigger = useRef<HTMLDivElement | null>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);

  const [filter, setFilter] = useState<AssignmentFilter>('toDo');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // This query fetches assignments with all status filter to determine whether the student
  // has any assignments at all — used to show contextual empty state messaging.
  const {assignments: anyAssignments, loading: loadingAll} = useAssignments(classroomId, 'all');

  const {assignments, fetchMore, isError, isLoadingMore, loading, refetch} = useAssignments(
    classroomId,
    filter,
    debouncedSearchQuery
  );

  function onFilterChange(newFilter: AssignmentFilter) {
    // Loaded pages are keyed by query variables inside useAssignments, so the
    // filter change evicts stale pages without an explicit reset.
    setFilter(newFilter);
  }

  function onBannerFilterSelect(newFilter: AssignmentFilter) {
    onFilterChange(newFilter);
    // The banner unmounts with the focused link, so focus moves to the newly selected filter.
    requestAnimationFrame(() => {
      toolbarRef.current?.querySelector<HTMLButtonElement>('[aria-pressed="true"]')?.focus();
    });
  }

  const isLoading = areClassroomsLoading || loading || loadingAll;
  const isDebouncing = searchQuery !== debouncedSearchQuery;
  const showContent = !isLoading && !isDebouncing;
  const showToDoBanner = filter === 'toDo' && assignments.length > 0 && anyAssignments.length > 1;

  const fetchMoreRef = useRef(fetchMore);
  useEffect(() => {
    fetchMoreRef.current = fetchMore;
  });

  const stableHandleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0];
      if (filter !== 'toDo' && entry?.isIntersecting) {
        fetchMoreRef.current();
      }
    },
    [filter]
  );

  useEffect(() => {
    const localPaginationTrigger = paginationTrigger?.current;
    const root = document.querySelector('#tabContentContainer');

    const observer = new IntersectionObserver(stableHandleIntersection, {
      root,
      rootMargin: '0px 0px 200px 0px',
      threshold: 0
    });

    if (!isNil(localPaginationTrigger)) {
      observer.observe(localPaginationTrigger);
    }

    return () => {
      if (!isNil(localPaginationTrigger)) {
        observer.unobserve(localPaginationTrigger);
        observer.disconnect();
      }
    };
  }, [stableHandleIntersection]);

  return (
    <>
      <ReviewGradeBanner classroomId={classroomId} />
      <div className='gap-x2 px-x1 py-x3 m:px-x4 relative flex flex-col overflow-hidden'>
        <div className='mb-x1 gap-x1-half flex flex-col'>
          <div ref={toolbarRef} className='gap-x1 flex flex-wrap items-center justify-between'>
            <FilterControl
              isScopedToClassroom={classroomId !== undefined}
              onChange={onFilterChange}
              value={filter}
            />
            <AssignmentSearch
              disabled={isLoading}
              onChange={setSearchQuery}
              onReset={() => setSearchQuery('')}
            />
          </div>
          {filter !== 'all' && (
            <Text size='s' color='tertiary'>
              {FILTER_DESCRIPTIONS[filter]}
            </Text>
          )}
        </div>
        {(isLoading || isDebouncing) && <LoadingSpinner bottomMarginLevel={0} size={3} />}
        {showContent && isError && (
          <TabEmptyState
            copy="We couldn't load your assignments."
            subtitle='Something went wrong. Check your connection and try again.'
            icon={['fal', 'circle-exclamation']}
            onRetry={() => {
              // refetch() rejects on a repeat failure (errorPolicy 'none'); the inline panel
              // already reflects the error via the query's `error`, so swallow the rejection.
              void refetch().catch(() => {});
            }}
          />
        )}
        {showContent && !isError && assignments.length === 0 && !debouncedSearchQuery && (
          <TabEmptyState
            copy={getEmptyStateCopy(filter, anyAssignments.length > 0)}
            icon={['fal', 'file-alt']}
            subtitle={
              anyAssignments.length > 0
                ? 'Expecting to see more assignments? Try another filter above.'
                : undefined
            }
          />
        )}
        {showContent && !isError && assignments.length === 0 && debouncedSearchQuery && (
          <TabEmptyState
            copy={`No assignments found with "${debouncedSearchQuery}".`}
            subtitle='Not seeing an assignment you’re expecting? Try another filter above.'
          />
        )}
        {showContent &&
          !isError &&
          assignments.length > 0 &&
          assignments.map((assignment) => (
            <Assignment
              assignment={assignment}
              hasTeacherFeedback={canReadTeacherFeedback(assignment)}
              key={assignment.assignmentId}
            />
          ))}
        {showContent && !isError && showToDoBanner && (
          <ToDoBanner onSelectFilter={onBannerFilterSelect} />
        )}
        {showContent && (
          <div
            className={classNames(
              'absolute flex w-full justify-center transition-[bottom] duration-200',
              {
                'bottom-x3': isLoadingMore,
                '-bottom-x7': !isLoadingMore
              }
            )}
          >
            <div className='h-x7 w-x7 background-subtle p-x1 elevation-shadow-l2 flex items-center rounded-full'>
              <LoadingSpinner bottomMarginLevel={0} size={2} />
            </div>
          </div>
        )}
      </div>
      <div ref={paginationTrigger} className='h-px' />
    </>
  );
}
